import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { useAuthStore } from '@/core/stores/auth.store';
import { usePostLoginRedirect } from '@/core/hooks/usePostLoginRedirect';

export function AuthCallbackPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const loading = useAuthStore((s) => s.loading);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const authStatus = useAuthStore((s) => s.authStatus);
  const authErrorKind = useAuthStore((s) => s.authErrorKind);
  const logout = useAuthStore((s) => s.logout);

  // useAuthInit (montado en el root) gestiona la sesión OAuth.
  // Aquí solo esperamos a que termine para lanzar el redirect.
  usePostLoginRedirect(!loading && hasHydrated && authStatus !== 'error');

  const handleBackToLogin = async () => {
    await logout().catch(() => {});
    navigate('/login', { replace: true });
  };

  if (authStatus === 'error') {
    // 'auth' is terminal (the session was already cleared + signed out): only offer
    // a fresh login. 'network' is transient: a reload re-runs the whole bootstrap.
    const isTerminal = authErrorKind === 'auth';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="space-y-1">
          <p className="text-foreground text-base font-medium">
            {isTerminal ? t('callback.errorAuthTitle') : t('callback.errorNetworkTitle')}
          </p>
          <p className="text-muted-foreground text-sm">
            {isTerminal
              ? t('callback.errorAuthDescription')
              : t('callback.errorNetworkDescription')}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleBackToLogin}
            data-testid="auth-callback-back-to-login"
          >
            {t('callback.backToLogin')}
          </Button>
          {!isTerminal && (
            <Button onClick={() => window.location.reload()} data-testid="auth-callback-retry">
              {t('callback.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (loading || !hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground text-sm">{t('callback.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground text-sm">{t('callback.redirecting')}</p>
    </div>
  );
}
